import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Building2, Phone, MapPin, Eye, EyeOff } from 'lucide-react';
import { Country } from 'country-state-city';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { SearchableSelect } from './ui/searchable-select';
import { authApi } from '../services/api';
import { LOGO_URL } from '../constants/brand';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { extractEmailDomain, isCommonEmailDomain } from '../lib/publicEmailDomains';
import {
  getCityOptionsForCountry,
  getCountryIsoCode,
  getPhoneCodeForCountry,
  useCanonicalCountryOptions,
} from '../utils/countries';

function normalizePhoneCode(phonecode) {
  if (!phonecode) return '+1';
  const part = String(phonecode).split('-')[0].trim();
  return part.startsWith('+') ? part : `+${part}`;
}

const Login = ({ onLogin, defaultTab = 'login' }) => {
  const [searchParams] = useSearchParams();
  const isRegisterOnlyPage = defaultTab === 'register';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);
  const [isOtherCity, setIsOtherCity] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralContextToken, setReferralContextToken] = useState('');
  const [referralContextError, setReferralContextError] = useState('');

  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    company_name: '',
    phone: '',
    phone_country_code: '+1',
    country: '',
    city: '',
    referral_code: ''
  });

  // Single-session: show message when logged out because user signed in elsewhere
  useEffect(() => {
    const reason = sessionStorage.getItem('logout_reason');
    if (reason === 'logged_in_elsewhere') {
      sessionStorage.removeItem('logout_reason');
      toast.info('You were signed out because you signed in elsewhere.');
    }
  }, []);

  useEffect(() => {
    const ref = searchParams.get('ref');
    const fullName = searchParams.get('full_name');
    const companyName = searchParams.get('company_name');
    const emailParam = searchParams.get('email');
    const phoneParam = searchParams.get('phone');

    const updates = {};
    if (ref && ref.trim()) updates.referral_code = ref.trim();
    if (fullName != null) {
      try {
        updates.full_name = decodeURIComponent(fullName);
      } catch {
        updates.full_name = fullName;
      }
    }
    if (companyName != null) {
      try {
        updates.company_name = decodeURIComponent(companyName);
      } catch {
        updates.company_name = companyName;
      }
    }
    if (emailParam != null) {
      try {
        updates.email = decodeURIComponent(emailParam);
      } catch {
        updates.email = emailParam;
      }
    }
    if (phoneParam != null) {
      try {
        updates.phone = decodeURIComponent(phoneParam);
      } catch {
        updates.phone = phoneParam;
      }
    }

    if (Object.keys(updates).length > 0) {
      setRegisterForm((prev) => ({ ...prev, ...updates }));
      setActiveTab('register');
    }
  }, [searchParams]);

  const [isRegisterPasswordFocused, setIsRegisterPasswordFocused] = useState(false);

  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const referralCodeFromUrl = (searchParams.get('ref') || '').trim();
  const isReferralLinkRegistration = Boolean(referralCodeFromUrl);

  useEffect(() => {
    const returnTo = searchParams.get('returnTo');
    if (
      returnTo &&
      returnTo.startsWith('/quotes?inquiry=') &&
      !returnTo.startsWith('//')
    ) {
      sessionStorage.setItem('post_auth_return_to', returnTo);
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    const loadReferralContext = async () => {
      if (!isReferralLinkRegistration) {
        setReferralContextToken('');
        setReferralContextError('');
        return;
      }
      try {
        setReferralContextError('');
        const response = await authApi.getReferralContext(referralCodeFromUrl);
        if (!cancelled) {
          setReferralContextToken(response.data?.referral_context_token || '');
          const normalizedCode = (response.data?.referral_code || '').trim();
          if (normalizedCode) {
            setRegisterForm((prev) => ({ ...prev, referral_code: normalizedCode }));
          }
        }
      } catch {
        if (!cancelled) {
          setReferralContextToken('');
          setReferralContextError('Unable to validate referral link. Please reopen the invitation link.');
        }
      }
    };
    loadReferralContext();
    return () => {
      cancelled = true;
    };
  }, [isReferralLinkRegistration, referralCodeFromUrl]);

  // Country / City dropdowns (same pattern as CompanyRegistration)
  const countryList = useCanonicalCountryOptions();

  const countryIsoCode = useMemo(() => {
    return getCountryIsoCode(registerForm.country);
  }, [registerForm.country]);

  const cityList = useMemo(() => {
    if (!countryIsoCode) return [];
    return getCityOptionsForCountry(registerForm.country);
  }, [countryIsoCode, registerForm.country]);

  const cityIsOther = isOtherCity || (registerForm.city && !cityList.some((c) => c.value === registerForm.city));
  const citySelectValue = cityIsOther ? '__other__' : (registerForm.city || '');

  // Phone country codes from country-state-city (for dropdown; default from selected country)
  const phoneCodeOptions = useMemo(() => {
    const seen = new Set();
    return Country.getAllCountries()
      .filter((c) => c.phonecode)
      .map((c) => {
        const code = normalizePhoneCode(c.phonecode);
        if (seen.has(code)) return null;
        seen.add(code);
        return { value: code, label: `${code} ${c.name}` };
      })
      .filter(Boolean);
  }, []);

  const handleRegisterCountryChange = (value) => {
    const countryName = value || '';
    const defaultCode = getPhoneCodeForCountry(countryName);
    setIsOtherCity(false);
    setRegisterForm((prev) => ({
      ...prev,
      country: countryName,
      city: '',
      phone_country_code: defaultCode,
    }));
  };

  const handleRegisterCityChange = (value) => {
    if (value === '__other__') {
      setIsOtherCity(true);
      setRegisterForm((prev) => ({ ...prev, city: '' }));
      return;
    }
    setIsOtherCity(false);
    setRegisterForm((prev) => ({ ...prev, city: value || '' }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowResendVerification(false);

    try {
      if (isCommonEmailDomain(loginForm.email)) {
        const domain = extractEmailDomain(loginForm.email);
        toast.error(`Login is not allowed with public email domains like @${domain}. Please use your company or workspace email.`);
        return;
      }

      const response = await authApi.login(loginForm);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('member', JSON.stringify(response.data.member));
      toast.success('Login successful!');
      if (onLogin) onLogin(response.data);
    } catch (error) {
      const errorMessage = getErrorMessage(error.response?.data?.detail, 'Login failed');
      toast.error(errorMessage);

      // Show resend verification button if email not verified
      if (typeof errorMessage === 'string' && (errorMessage.includes('verify your email') || errorMessage.includes('email address'))) {
        setShowResendVerification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!loginForm.email) {
      toast.error('Please enter your email address');
      return;
    }

    setResendLoading(true);
    try {
      await authApi.resendVerification(loginForm.email);
      toast.success('Verification email sent! Please check your inbox.');
      setShowResendVerification(false);
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setResendLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const passwordHasMinLength = registerForm.password.length >= 8;
    const passwordHasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(registerForm.password);

    if (!passwordHasMinLength || !passwordHasSpecialChar) {
      toast.error('Password must be at least 8 characters and include at least one special character');
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (isCommonEmailDomain(registerForm.email)) {
      const domain = extractEmailDomain(registerForm.email);
      toast.error(`Registration requires a company or workspace email. Public email domains like @${domain} are not allowed.`);
      return;
    }

    if (!registerForm.country?.trim()) {
      toast.error('Please select a country');
      return;
    }
    if (!registerForm.city?.trim()) {
      toast.error('Please select or enter a city');
      return;
    }
    const phoneNumber = (registerForm.phone || '').replace(/\s/g, '');
    if (!phoneNumber || phoneNumber.length < 7) {
      toast.error('Phone number is required (at least 7 digits)');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, phone_country_code, phone, ...rest } = registerForm;
      const fullPhone = registerForm.phone.trim().startsWith('+')
        ? registerForm.phone.trim().replace(/\s/g, '')
        : (phone_country_code || '+1') + phoneNumber;
      const userData = {
        ...rest,
        phone: fullPhone,
      };
      if (isReferralLinkRegistration) {
        if (!referralContextToken) {
          toast.error('Referral link validation failed. Please reopen your referral link.');
          return;
        }
        userData.referral_context_token = referralContextToken;
      }
      if (!userData.referral_code || !userData.referral_code.trim()) {
        delete userData.referral_code;
      }
      const response = await authApi.register(userData);
      toast.success('Registration successful! Please check your email to verify your account.');
      setActiveTab('login');
      setRegisterForm({
        email: '',
        password: '',
        confirmPassword: '',
        full_name: '',
        company_name: '',
        phone: '',
        phone_country_code: '+1',
        country: '',
        city: '',
        referral_code: ''
      });
      setIsOtherCity(false);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo - keep visitors in the member portal instead of the WordPress site at /. */}
        <Link to="/login" className="block text-center mb-8 cursor-pointer hover:opacity-90 transition-opacity outline-none focus:outline-none focus-visible:outline-none focus:ring-0">
          <img
            src={LOGO_URL}
            alt="One Globe Alliance"
            className="mx-auto mb-4 h-20 w-auto object-contain"
          />
          <h1 className="text-3xl font-bold text-white">One Globe Alliance</h1>
          <p className="text-blue-200 mt-2">Members Portal</p>
        </Link>

        <Card className="shadow-2xl">
          <CardHeader className="pb-0">
            <Tabs value={activeTab} onValueChange={isRegisterOnlyPage ? undefined : setActiveTab}>
              {!isRegisterOnlyPage && (
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login">Login</TabsTrigger>
                  <TabsTrigger value="register">Register</TabsTrigger>
                </TabsList>
              )}

              <TabsContent value="login" className="mt-4">
                <CardTitle className="text-xl">Welcome Back</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Sign in to access your account</p>
              </TabsContent>

              <TabsContent value="register" className="mt-4">
                <CardTitle className="text-xl">Create Account</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Join the global logistics network</p>
              </TabsContent>
            </Tabs>
          </CardHeader>

          <CardContent className="pt-4">
            <Tabs value={activeTab}>
              {/* Login Form */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">Email Address</Label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="login-email"
                        type="email"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        placeholder="you@company.com"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        placeholder="••••••••"
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center text-sm">
                      <input type="checkbox" className="mr-2" />
                      Remember me
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>

                  {showResendVerification && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 mb-2">
                        Your email address has not been verified yet.
                      </p>
                      <Button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendLoading}
                        variant="outline"
                        className="w-full text-sm"
                        size="sm"
                      >
                        {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                      </Button>
                    </div>
                  )}
                </form>

                {/* Demo Login */}
                <div className="mt-6 pt-4 border-t">
                  <p className="text-xs text-gray-500 text-center mb-3">Demo Access</p>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setLoginForm({ email: 'demo@oneglobealliance.com', password: 'demo1234' });
                    }}
                  >
                    Use Demo Credentials
                  </Button>
                </div>
              </TabsContent>

              {/* Register Form */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="reg-name">Full Name *</Label>
                      <div className="relative">
                        <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                          id="reg-name"
                          value={registerForm.full_name}
                          onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                          placeholder="John Smith"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="reg-company">Company Name *</Label>
                      <div className="relative">
                        <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                          id="reg-company"
                          value={registerForm.company_name}
                          onChange={(e) => setRegisterForm({ ...registerForm, company_name: e.target.value })}
                          placeholder="Your Company Ltd"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="reg-email">Email Address *</Label>
                      <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                          id="reg-email"
                          type="email"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                          placeholder="you@company.com"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="reg-password">Password *</Label>
                      <div className="relative">
                        <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                          id="reg-password"
                          type={showRegisterPassword ? 'text' : 'password'}
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                          required
                          minLength={8}
                          onFocus={() => setIsRegisterPasswordFocused(true)}
                          onBlur={() => setIsRegisterPasswordFocused(false)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showRegisterPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {isRegisterPasswordFocused && (
                        <p className="mt-1 text-xs text-gray-500">
                          Password must be at least 8 characters and include at least one special character.
                        </p>
                      )}
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="reg-confirm">Confirm Password *</Label>
                      <div className="relative">
                        <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                          id="reg-confirm"
                          type={showRegisterConfirmPassword ? 'text' : 'password'}
                          value={registerForm.confirmPassword}
                          onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterConfirmPassword(!showRegisterConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showRegisterConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="reg-country">Country *</Label>
                      <SearchableSelect
                        options={countryList}
                        value={registerForm.country || ''}
                        onChange={handleRegisterCountryChange}
                        placeholder="Select Country"
                        allowOther={false}
                        error={false}
                      />
                    </div>

                    <div>
                      <Label htmlFor="reg-city">City *</Label>
                      <SearchableSelect
                        options={cityList}
                        value={citySelectValue}
                        onChange={handleRegisterCityChange}
                        placeholder={countryIsoCode ? 'Search or select city' : 'Select country first'}
                        allowOther={!!countryIsoCode}
                        disabled={!countryIsoCode}
                        error={false}
                      />
                      {citySelectValue === '__other__' && (
                        <Input
                          className="mt-2"
                          value={registerForm.city}
                          onChange={(e) => setRegisterForm({ ...registerForm, city: e.target.value })}
                          placeholder="Type city"
                        />
                      )}
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="reg-phone">Phone Number *</Label>
                      <div className="flex gap-2">
                        <select
                          id="reg-phone-code"
                          className="w-28 px-2 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-800 bg-transparent"
                          value={registerForm.phone_country_code}
                          onChange={(e) => setRegisterForm({ ...registerForm, phone_country_code: e.target.value })}
                          aria-label="Country code"
                        >
                          {phoneCodeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.value}
                            </option>
                          ))}
                        </select>
                        <div className="relative flex-1">
                          <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <Input
                            id="reg-phone"
                            type="tel"
                            value={registerForm.phone}
                            onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                            placeholder="234 567 8900"
                            className="pl-10"
                            required
                            minLength={7}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Country code updates when you select a country; you can change it here.</p>
                    </div>

                    <div className="col-span-2">
                      <Label htmlFor="reg-referral">Referral Code (Optional)</Label>
                      <Input
                        id="reg-referral"
                        value={registerForm.referral_code}
                        onChange={(e) => setRegisterForm({ ...registerForm, referral_code: e.target.value })}
                        placeholder="e.g. OGA-XXXXXXXXXX"
                        className="font-mono"
                        readOnly={isReferralLinkRegistration}
                        aria-readonly={isReferralLinkRegistration}
                      />
                      {isReferralLinkRegistration && registerForm.referral_code && (
                        <p className="text-xs text-gray-500 mt-1">Referral code applied via invitation link</p>
                      )}
                      {!isReferralLinkRegistration && (
                        <p className="text-xs text-gray-500 mt-1">Enter the code if you received one from a member</p>
                      )}
                      {referralContextError && (
                        <p className="text-xs text-red-600 mt-1">{referralContextError}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start">
                    <input type="checkbox" className="mt-1 mr-2" required />
                    <span className="text-xs text-gray-500">
                      I agree to the{' '}
                      <a
                        href="https://oneglobealliance.com/terms-and-conditions/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Terms and Conditions
                      </a>
                      {' '}and{' '}
                      <a
                        href="https://oneglobealliance.com/privacy-policy/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-blue-200 text-sm mt-6">
          © 2026 One Globe Alliance. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
